import { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { ModalCompAdvance } from "../../Ui/ModalComp";
import { TextInputComp } from "../..";
import InvestorPortalActionButton from "./InvestorPortalActionButton";
import uploadImage from "../../../utils/uploadImage";

const schema = Yup.object({
  type: Yup.string().oneOf(["written", "video"]).required(),
  name: Yup.string().trim().required("Name is required"),
  role: Yup.string().trim(),
  investmentType: Yup.string().trim(),
  quote: Yup.string().when("type", {
    is: "written",
    then: Yup.string().trim().required("Quote is required"),
    otherwise: Yup.string().trim(),
  }),
  imageUrl: Yup.string().trim(),
  videoUrl: Yup.string().trim(),
  youtubeUrl: Yup.string().trim(),
  sortOrder: Yup.number().min(0),
  isPublished: Yup.boolean(),
  isFeatured: Yup.boolean(),
}).test("video-media", "Provide a YouTube URL/ID or video URL", function (values) {
  if (values?.type !== "video") return true;
  const hasYoutube = Boolean(values.youtubeUrl?.trim());
  const hasVideo = Boolean(values.videoUrl?.trim());
  return hasYoutube || hasVideo;
});

async function uploadMediaFile(file, resourceType = "image") {
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || "afrilocales";
  const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || "svmprhvf";
  const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudName}/${resourceType}/upload`;
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", uploadPreset);
  const response = await fetch(uploadUrl, { method: "POST", body: formData });
  const data = await response.json();
  if (!response.ok || !data?.secure_url) {
    throw new Error(data?.error?.message || "Upload failed");
  }
  return data.secure_url;
}

export default function AdminTestimonialModal({ show, testimonial, onClose, onSubmit }) {
  const isEdit = Boolean(testimonial?.id);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  const body = (
    <Formik
      initialValues={{
        type: testimonial?.type || "written",
        name: testimonial?.name || "",
        role: testimonial?.role || "",
        investmentType: testimonial?.investmentType || "",
        quote: testimonial?.quote || "",
        imageUrl: testimonial?.imageUrl || "",
        videoUrl: testimonial?.videoUrl || "",
        youtubeUrl: testimonial?.youtubeId || "",
        sortOrder: testimonial?.sortOrder ?? 0,
        isPublished: testimonial?.isPublished !== false,
        isFeatured: Boolean(testimonial?.isFeatured),
      }}
      validationSchema={schema}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await onSubmit(values);
          onClose();
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, setFieldValue, isSubmitting, errors, touched }) => (
        <Form>
          <div className="mb-3">
            <label className="form-label small fw-semibold">Testimonial type</label>
            <div className="d-flex flex-column gap-2">
              {[
                { value: "written", label: "Written testimonial", hint: "Quote with optional portrait image." },
                { value: "video", label: "Video testimonial", hint: "YouTube link or uploaded/hosted video URL." },
              ].map((opt) => (
                <label key={opt.value} className="border rounded-3 p-3 mb-0" style={{ cursor: "pointer" }}>
                  <input
                    type="radio"
                    className="form-check-input me-2"
                    name="type"
                    checked={values.type === opt.value}
                    onChange={() => setFieldValue("type", opt.value)}
                  />
                  <strong>{opt.label}</strong>
                  <div className="small text-muted mt-1 ms-4">{opt.hint}</div>
                </label>
              ))}
            </div>
          </div>

          <TextInputComp
            labelText="Full name"
            name="name"
            placeholder="e.g. Adaeze Okonkwo"
            required
          />
          <TextInputComp
            labelText="Role / profession"
            name="role"
            placeholder="e.g. Pharmacist, Lagos"
          />

          {values.type === "written" ? (
            <>
              <TextInputComp
                labelText="Investment type"
                name="investmentType"
                placeholder="e.g. Real Estate"
              />
              <div className="mb-3">
                <label className="form-label small fw-semibold">
                  Quote <span className="text-danger">*</span>
                </label>
                <textarea
                  className="form-control"
                  rows={4}
                  name="quote"
                  placeholder="What the investor said…"
                  value={values.quote}
                  onChange={(e) => setFieldValue("quote", e.target.value)}
                />
                {touched.quote && errors.quote ? (
                  <small className="text-danger">{errors.quote}</small>
                ) : null}
              </div>
              <TextInputComp
                labelText="Portrait image URL (optional)"
                name="imageUrl"
                placeholder="https://…"
              />
              <div className="mb-3">
                <label className="form-label small fw-semibold">Or upload portrait</label>
                <input
                  type="file"
                  accept="image/*"
                  className="form-control"
                  disabled={uploadingImage}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setUploadingImage(true);
                    try {
                      const url = await uploadImage(file);
                      if (url) setFieldValue("imageUrl", url);
                      toast.success("Image uploaded");
                    } catch (err) {
                      toast.error(err?.message || "Image upload failed");
                    } finally {
                      setUploadingImage(false);
                      e.target.value = "";
                    }
                  }}
                />
                {uploadingImage ? <small className="text-muted">Uploading…</small> : null}
                {values.imageUrl ? (
                  <img
                    src={values.imageUrl}
                    alt=""
                    className="mt-2 rounded-circle"
                    style={{ width: 64, height: 64, objectFit: "cover" }}
                  />
                ) : null}
              </div>
              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="featuredCheck"
                  checked={values.isFeatured}
                  onChange={(e) => setFieldValue("isFeatured", e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="featuredCheck">
                  Feature on homepage
                </label>
              </div>
            </>
          ) : (
            <>
              <div className="mb-3">
                <label className="form-label small fw-semibold">Quote (optional)</label>
                <textarea
                  className="form-control"
                  rows={3}
                  name="quote"
                  placeholder="Short caption for the video…"
                  value={values.quote}
                  onChange={(e) => setFieldValue("quote", e.target.value)}
                />
              </div>
              <TextInputComp
                labelText="YouTube URL or ID"
                name="youtubeUrl"
                placeholder="https://youtube.com/watch?v=… or 11-char ID"
              />
              <TextInputComp
                labelText="Video file URL"
                name="videoUrl"
                placeholder="https://…/video.mp4"
              />
              <div className="mb-3">
                <label className="form-label small fw-semibold">Or upload video</label>
                <input
                  type="file"
                  accept="video/*"
                  className="form-control"
                  disabled={uploadingVideo}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setUploadingVideo(true);
                    try {
                      const url = await uploadMediaFile(file, "video");
                      setFieldValue("videoUrl", url);
                      toast.success("Video uploaded");
                    } catch (err) {
                      toast.error(err?.message || "Video upload failed");
                    } finally {
                      setUploadingVideo(false);
                      e.target.value = "";
                    }
                  }}
                />
                {uploadingVideo ? <small className="text-muted">Uploading…</small> : null}
              </div>
              {typeof errors === "object" && errors.videoMedia ? (
                <small className="text-danger d-block mb-2">{errors.videoMedia}</small>
              ) : null}
            </>
          )}

          <TextInputComp
            labelText="Sort order"
            name="sortOrder"
            type="number"
            placeholder="0"
          />
          <small className="text-muted d-block mb-3" style={{ marginTop: "-0.5rem" }}>
            Lower numbers appear first on the website.
          </small>
          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="publishedCheck"
              checked={values.isPublished}
              onChange={(e) => setFieldValue("isPublished", e.target.checked)}
            />
            <label className="form-check-label small" htmlFor="publishedCheck">
              Published on website
            </label>
          </div>

          <div className="d-flex gap-2 justify-content-end">
            <InvestorPortalActionButton
              btnText="Cancel"
              type="button"
              variant="ghost"
              fullWidth={false}
              onClick={onClose}
            />
            <InvestorPortalActionButton
              btnText={isEdit ? "Save changes" : "Create testimonial"}
              type="submit"
              variant="primary"
              fullWidth={false}
              disabled={isSubmitting || uploadingImage || uploadingVideo}
              loading={isSubmitting}
            />
          </div>
        </Form>
      )}
    </Formik>
  );

  return (
    <ModalCompAdvance
      show={show}
      handleClose={onClose}
      modalTitle={isEdit ? "Edit testimonial" : "Add testimonial"}
      close
      size="lg"
      centered
      arrayComp={[{ component: body }]}
    />
  );
}
